// Demo build only (see src/demo/): seeds the in-memory clinic and applies the
// language and theme the portal frame asked for. MUST stay the first import —
// the modules below read both out of localStorage as they evaluate. In every
// other build `@demo` resolves to an empty stand-in.
import '@demo/boot'

// On-premise build only (see src/local/): mints this browser's install id, asks
// the browser to make its storage persistent, and configures telemetry. MUST
// also stay above the imports below — the first screen already reads from
// IndexedDB, and the install id has to exist before anything reports anything.
// In every other build `@local` resolves to an empty stand-in.
import '@local/boot'

import { createApp, defineAsyncComponent } from 'vue'
import router from './router'
import { createI18n } from 'vue-i18n'
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { VDateInput } from 'vuetify/labs/VDateInput'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { vMaska } from "maska/vue"
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'

// Praxio Design System
import './assets/themes/index.css'

// Import translations
import messages, { preloadAllDomains, loadDomains, AVAILABLE_LOCALES } from './messages.js'
import { setupRouterGuard } from './routerTranslations.js'
import { useFeatures } from './composables/useFeatures.js'
import { registerServiceWorker } from './registerServiceWorker.js'

// Components
import App from './App.vue'

// Suppress ResizeObserver loop errors (harmless warnings in dev mode)
const debounce = (callback, delay) => {
  let tid
  return function(...args) {
    const ctx = self
    tid && clearTimeout(tid)
    tid = setTimeout(() => {
      callback.apply(ctx, args)
    }, delay)
  }
}

const _ = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20)
    super(callback)
  }
}

// Detect user's browser language
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  // Extract the base language code (e.g., 'en' from 'en-US')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Check if we support this language, otherwise fallback to French
  const supportedLanguages = ['en', 'fr'];
  return supportedLanguages.includes(langCode) ? langCode : 'fr';
};

// Get locale from URL parameter (for SSO), localStorage, or browser settings.
//
// An explicit choice — the ?lang the portal signs a user in with, or the one
// already saved — is honoured for every locale that ships in src/locales/, the
// same list App.vue switches to at runtime and the demo seeds its clinic in.
// Auto-detection is deliberately narrower: a browser set to a language Praxio
// does not sell in yet should still open in French, which is what
// getBrowserLanguage above decides.
const getInitialLocale = () => {

  // Check URL for lang parameter (SSO from Praxio)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');

  const STORAGE_KEY = 'praxio_language'; // Use same key as Praxio for cross-tab sync

  if (urlLang && AVAILABLE_LOCALES.includes(urlLang)) {
    localStorage.setItem(STORAGE_KEY, urlLang);
    return urlLang;
  }

  // Check localStorage (use same key as Praxio)
  const savedLocale = localStorage.getItem(STORAGE_KEY);
  if (savedLocale && AVAILABLE_LOCALES.includes(savedLocale)) {
    return savedLocale;
  }

  // Fall back to browser language
  const browserLang = getBrowserLanguage();
  return browserLang;
};

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: 'en', // set fallback locale
  messages,   
  // something vue-i18n options here ...
})

// Shared Praxio brand blue palette — identical in light and dark so the brand
// accent stays consistent across themes. Only surfaces/backgrounds/text and the
// semantic status colors change between themes (see below).
const brandBlues = {
  praxioBlue: '#2563eb', // Praxio signature blue
  // Override all Vuetify blue color variations with Praxio blue palette
  blue: '#2563eb',
  'blue-lighten-5': '#d0defb',
  'blue-lighten-4': '#dbeafe',
  'blue-lighten-3': '#bfdbfe',
  'blue-lighten-2': '#93c5fd',
  'blue-lighten-1': '#60a5fa',
  'blue-darken-1': '#1d4ed8',
  'blue-darken-2': '#1e40af',
  'blue-darken-3': '#1e3a8a',
  'blue-darken-4': '#172554',
  // Override light-blue variations (Material Design naming)
  'light-blue': '#2563eb',
  'light-blue-lighten-5': '#d0defb',
  'light-blue-lighten-4': '#dbeafe',
  'light-blue-lighten-3': '#bfdbfe',
  'light-blue-lighten-2': '#93c5fd',
  'light-blue-lighten-1': '#60a5fa',
  'light-blue-darken-1': '#1d4ed8',
  'light-blue-darken-2': '#1e40af',
  'light-blue-darken-3': '#2563eb', // This was the problematic #0277BD
  'light-blue-darken-4': '#172554',
};

// Persisted preference picked up synchronously so the very first paint already
// matches the chosen theme (no light->dark flash). Kept in sync with the
// `.dark` <html> class (which drives the CSS-variable design tokens) below.
const prefersDark = localStorage.getItem('darkMode') === 'true';
if (prefersDark) {
  document.documentElement.classList.add('dark');
}

const vuetify = createVuetify({
  theme: {
    defaultTheme: prefersDark ? 'customThemeDark' : 'customTheme',
    themes: {
      customTheme: {
        dark: false,
        colors: {
          primary: '#455a64', // blue-grey-darken-1
          ...brandBlues,
        }
      },
      // Dark counterpart. Vuetify derives readable on-surface/on-background text
      // automatically from `dark: true`; we supply coherent dark surfaces and
      // brightened status colors so contrast holds up on dark backgrounds.
      customThemeDark: {
        dark: true,
        colors: {
          ...brandBlues,
          primary: '#90a4ae', // blue-grey-lighten-2 — readable accent on dark
          praxioBlue: '#3b82f6', // slightly lifted brand blue for dark surfaces
          background: '#121212',
          surface: '#1e1e1e',
          'surface-bright': '#2a2a2c',
          'surface-light': '#2a2a2c',
          'surface-variant': '#3f3f46',
          'on-surface-variant': '#e4e4e7',
          error: '#f87171',
          success: '#4ade80',
          warning: '#fb923c',
          info: '#60a5fa',
        }
      }
    }
  },
  locale: {
    locale: 'en', // default locale
    fallback: 'en',
   /*  messages: { en, fr }, */
  },   
  icons: {
    defaultSet: 'mdi',
    aliases,

  },
  components,
  directives,
})

let app = createApp(App)

// Retroact console branding
console.log(
  `%c
  █▀▄ █▀▀ ▀█▀ █▀▄ █▀█ █▀█ █▀▀ ▀█▀   █▀█ █▀█ █▀█
  █▀▄ █▀▀  █  █▀▄ █ █ █▀█ █    █  ▄ █▀█ █▀▀ █▀▀
  ▀ ▀ ▀▀▀  ▀  ▀ ▀ ▀▀▀ ▀ ▀ ▀▀▀  ▀    ▀ ▀ ▀   ▀  

  Made with love ❤️ • Vue.js 3 • Vuetify 3 • ApexCharts • Vite • Firebase • Google Cloud • Node.js • MySQL
  `,
  'color: #1976D2; font-weight: bold;'
);

// ApexCharts - lazy loaded so the ~heavy apexcharts vendor code is only
// fetched when an <apexchart> is actually rendered (i.e. on the Trends route),
// instead of being pulled into the initial bootstrap bundle.
// We register the global `apexchart` component name (as vue3-apexcharts does)
// backed by a dynamic import.
const ApexChartAsync = defineAsyncComponent(async () => {
  const mod = await import('vue3-apexcharts')
  // The package's default export is the chart component itself.
  return mod.default || mod
})
app.component('apexchart', ApexChartAsync)

app.component('VDateInput',VDateInput)
app.directive("maska", vMaska)

app.use(i18n)
app.use(vuetify)
app.use(router)

// Setup route-based translation loading
setupRouterGuard(router, i18n)

app.mount('#app')

// Installability + a warm bundle cache on repeat launches. No-ops in the demo
// build and in development; see the file for the full list of guards.
registerServiceWorker()

// Resolve backend feature flags early (single source of truth for gated
// features such as the AI dose advisor). Fire-and-forget: refs default to OFF,
// so gated UI stays hidden until/unless the backend confirms it is enabled.
useFeatures().ensureLoaded()

// Preload remaining translations in background after initial render
// This ensures fast initial load while loading non-critical translations
setTimeout(() => {
  const currentLocale = i18n.global.locale.value
  
  // Preload auth, navigation and tickets immediately (likely needed soon)
  loadDomains(currentLocale, ['auth', 'navigation', 'tickets']).then(() => {
    i18n.global.setLocaleMessage(currentLocale, messages[currentLocale])
  })
  
  // Preload everything else after a short delay
  setTimeout(() => {
    preloadAllDomains(currentLocale).then(() => {
      i18n.global.setLocaleMessage(currentLocale, messages[currentLocale])
    })
  }, 2000)
}, 100)

//i18n.locale = 'en'